import { ChangeEvent, KeyboardEvent, useCallback, useRef } from 'react'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { useFocusMethod } from '@/hooks/useFocusMethod'

interface NumberFieldProps {
	value?: number
	onValueChange?: (value: number) => void
	min?: number
	max?: number
	step?: number
	label?: string
	prefix?: string
	placeholder?: string
	disabled?: boolean
	onClear?: () => void
	spacesClass?: string
	className?: string
}

export function NumberField ({
	value,
	onValueChange,
	min,
	max,
	step = 1,
	label,
	prefix,
	placeholder,
	disabled = false,
	onClear,
	spacesClass = '',
	className = ''
}: NumberFieldProps) {
	const allowNegative = min !== undefined && min < 0
	const effectiveMin = allowNegative ? min : (min ?? 0)

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const num = parseFloat(e.target.value)
			if (isNaN(num)) return
			if (!allowNegative && num < 0) return
			onValueChange?.(num)
		},
		[onValueChange, allowNegative]
	)

	// A number input reports "-" as an empty value, so block the key rather than sanitising after the fact.
	const handleKeyDown = useCallback(
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (!allowNegative && (e.key === '-' || e.key === 'e' || e.key === 'E')) {
				e.preventDefault()
			}
		},
		[allowNegative]
	)

	const wrapperRef = useRef<HTMLDivElement>(null)
	const rootClasses = [
		'spaces-number-field spaces:flex spaces:flex-col spaces:gap-8 spaces:font-default',
		spacesClass,
		className
	]
		.filter(Boolean)
		.join(' ')

	useFocusMethod(wrapperRef)

	return (
		<div className={rootClasses}>
			{label && <label className='spaces:text-16/24 spaces:text-content-2'>{label}</label>}

			{/* Input wrapper */}
			<div
				ref={wrapperRef}
				className={[
					'field-wrapper spaces:flex spaces:items-center spaces:gap-4 spaces:rounded-12 spaces:border spaces:border-canvas-2 spaces:pl-12 spaces:pr-4 spaces:py-4 spaces:text-16/24',
					disabled ? 'spaces-disabled' : '',
					disabled ? 'spaces:bg-canvas-4 spaces:cursor-not-allowed' : 'spaces:bg-canvas-base'
				]
					.filter(Boolean)
					.join(' ')}
			>
				{/* Content wrapper */}
				<span className='spaces:flex spaces:gap-4 spaces:py-4 spaces:w-full'>
					{prefix && value !== undefined && (
						<span className='spaces:text-content-3'>{prefix}</span>
					)}
					<input
						type='number'
						value={value ?? ''}
						placeholder={placeholder}
						onChange={handleChange}
						onKeyDown={handleKeyDown}
						min={effectiveMin}
						max={max}
						step={step}
						disabled={disabled}
						className='spaces:w-full spaces:bg-transparent spaces:placeholder:text-content-3 spaces:[appearance:textfield] spaces:[&::-webkit-outer-spin-button]:appearance-none spaces:[&::-webkit-inner-spin-button]:appearance-none spaces:disabled:cursor-not-allowed'
					/>
				</span>
				{value !== undefined && !disabled && (
					<Button
						appearance='ghost'
						color='content3'
						size='sm'
						iconOnly='X'
						type='button'
						aria-label='Clear value'
						onClick={onClear}
					/>
				)}
				{disabled && (
					<div className='spaces:flex spaces:items-center spaces:p-8'>
						<Icon name='Lock' size={16} className='spaces:text-content-3' />
					</div>
				)}
			</div>
		</div>
	)
}
